import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Briefcase, Users, Globe, Award, Building2, TrendingUp, Cpu, Plane, HeartPulse, DollarSign, Factory, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function HomePage() {
  return <div className="bg-white" data-id="element-478">
      {/* ─── HERO — Editorial Split Masthead ─────────────────── */}
      <section className="relative min-h-screen bg-navy-950 text-white overflow-hidden flex items-center" data-id="element-479">
        {/* Background — subtle */}
        <div className="absolute inset-0 z-0" data-id="element-480">
          <img src="https://images.unsplash.com/photo-1486325212027-8081e485255e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=80" alt="" className="w-full h-full object-cover opacity-[0.08]" data-id="element-481" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-950/95 to-navy-950" data-id="element-482" />
        </div>

        {/* Decorative gold orbs */}
        <div className="absolute top-0 left-0 w-[700px] h-[700px] bg-gold-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-483" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gold-500/[0.03] rounded-full blur-3xl translate-x-1/3 translate-y-1/3" data-id="element-484" />

        <div className="relative z-10 w-full pt-40 pb-28 md:pt-44 md:pb-32" data-id="element-485">
          <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-486">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-487">
              {/* LEFT — Headline + CTAs */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 1,
              ease: [0.22, 1, 0.36, 1]
            }} className="lg:col-span-7" data-id="element-488">
                {/* Eyebrow with animated underline */}
                <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.2,
                duration: 0.6
              }} className="flex items-center gap-4 mb-10" data-id="element-489">
                  <motion.div initial={{
                  width: 0
                }} animate={{
                  width: 48
                }} transition={{
                  delay: 0.4,
                  duration: 0.8
                }} className="h-px bg-gold-500" data-id="element-490" />
                  <span className="text-gold-500 font-semibold tracking-[0.35em] uppercase text-[11px]" data-id="element-491">
                    Global Executive Search & Talent Advisory
                  </span>
                </motion.div>

                {/* Headline */}
                <h1 className="font-serif font-bold leading-[0.98] tracking-tight mb-10 text-[44px] sm:text-6xl lg:text-[76px] xl:text-[86px]" data-id="element-492">
                  Connecting{' '}
                  <span className="italic text-gold-500 inline-block" data-id="element-493">
                    exceptional
                  </span>
                  <br className="hidden sm:block" data-id="element-494" />
                  <span className="block sm:inline" data-id="element-495">
                    {' '}
                    leadership talent with{' '}
                  </span>
                  <span className="text-white" data-id="element-496">high-growth</span>
                  <br className="hidden sm:block" data-id="element-497" />
                  organizations worldwide.
                </h1>

                {/* Subheadline */}
                <motion.p initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5,
                duration: 0.8
              }} className="text-lg md:text-xl text-gray-300 leading-relaxed mb-12 max-w-2xl font-light" data-id="element-498">
                  We help organizations secure transformative executive, board,
                  and strategic leadership talent globally — with the
                  discretion, precision, and judgment the most demanding
                  searches require.
                </motion.p>

                {/* CTAs */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.7,
                duration: 0.6
              }} className="flex flex-col sm:flex-row gap-4 mb-14" data-id="element-499">
                  <Button variant="gold" size="lg" href="mailto:contact@talentbridge.blog" data-id="element-500">
                    Book a Consultation
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-501" />
                  </Button>
                  <Button variant="outline" size="lg" href="/contact" className="text-white border-white/40 hover:bg-white hover:text-navy-900" data-id="element-502">
                    Submit Resume
                  </Button>
                </motion.div>

                {/* Inline trust strip */}
                <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 1,
                duration: 0.8
              }} className="flex flex-wrap items-center gap-6 md:gap-10 pt-8 border-t border-white/10" data-id="element-503">
                  <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-504">
                    <div className="w-2 h-2 rounded-full bg-gold-500 animate-pulse" data-id="element-505" />
                    <span className="tracking-wider uppercase" data-id="element-506">
                      Available for new mandates
                    </span>
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-507">
                    <span className="text-gold-500 font-semibold" data-id="element-508">AESC</span>{' '}
                    Member Firm
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-509">
                    <span className="text-white" data-id="element-510">ISO 27001</span> Certified
                  </div>
                </motion.div>
              </motion.div>

              {/* RIGHT — Premium executive imagery + floating credentials */}
              <motion.div initial={{
              opacity: 0,
              x: 40
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 1,
              delay: 0.4,
              ease: [0.22, 1, 0.36, 1]
            }} className="lg:col-span-5 hidden lg:block relative" data-id="element-511">
                <div className="relative" data-id="element-512">
                  {/* Decorative offset frame */}
                  <div className="absolute -top-6 -left-6 w-full h-full border border-gold-500/40" data-id="element-513" />
                  <div className="absolute -bottom-6 -right-6 w-full h-full border border-white/10" data-id="element-514" />

                  {/* Main image */}
                  <div className="relative aspect-[4/5] overflow-hidden" data-id="element-515">
                    <img src="https://images.unsplash.com/photo-1664575602276-acd073f104c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=85" alt="Executive leadership" className="w-full h-full object-cover" data-id="element-516" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/20 to-transparent" data-id="element-517" />

                    {/* Floating stat — top */}
                    <motion.div initial={{
                    opacity: 0,
                    y: -20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 1.2,
                    duration: 0.6
                  }} className="absolute top-6 left-6 right-6 bg-navy-900/80 backdrop-blur-md border border-gold-500/30 p-5" data-id="element-518">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-gold-500 mb-1 font-semibold" data-id="element-519">
                        Live Mandate
                      </div>
                      <div className="text-white text-sm font-serif" data-id="element-520">
                        CEO Search · Industrial Technology · Global
                      </div>
                    </motion.div>

                    {/* Floating stat — bottom */}
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 1.4,
                    duration: 0.6
                  }} className="absolute bottom-6 left-6 right-6 bg-white text-navy-900 p-6" data-id="element-521">
                      <div className="grid grid-cols-3 divide-x divide-gray-200" data-id="element-522">
                        <div className="pr-3" data-id="element-523">
                          <div className="text-2xl font-serif font-bold" data-id="element-524">
                            500+
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 leading-tight" data-id="element-525">
                            Placements
                          </div>
                        </div>
                        <div className="px-3" data-id="element-526">
                          <div className="text-2xl font-serif font-bold" data-id="element-527">
                            42
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 leading-tight" data-id="element-528">
                            Countries
                          </div>
                        </div>
                        <div className="pl-3" data-id="element-529">
                          <div className="text-2xl font-serif font-bold" data-id="element-530">
                            94%
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1 leading-tight" data-id="element-531">
                            Retention
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.6,
          duration: 1
        }} className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-3 text-white/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-532">
            <span data-id="element-533">Explore</span>
            <motion.div animate={{
            scaleY: [0.3, 1, 0.3]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-px h-12 bg-gradient-to-b from-gold-500 via-white/40 to-transparent origin-top" data-id="element-534" />
          </motion.div>
        </div>
      </section>

      {/* ─── CREDIBILITY STRIP — "Trusted by" ─────────────────── */}
      <section className="bg-white border-b border-gray-100 py-12" data-id="element-535">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-536">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-16" data-id="element-537">
            <div className="flex items-center gap-3 flex-shrink-0" data-id="element-538">
              <div className="w-10 h-px bg-gold-500" data-id="element-539" />
              <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-[11px] whitespace-nowrap" data-id="element-540">
                Trusted by leadership teams at
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-8 gap-y-4 flex-1" data-id="element-541">
              {['Fortune 500 Industrials', 'Global Healthcare Systems', 'PE Portfolio Companies', 'Sovereign Wealth Funds', 'Tech Unicorns', 'Family Offices'].map((client, i) => <div key={i} className="text-sm font-serif text-navy-900/70 hover:text-navy-900 transition-colors tracking-tight" data-id="element-542">
                  {client}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ─── TRUST METRICS ─────────────────────────────────────── */}
      <section className="py-20 bg-white border-b border-gray-100" data-id="element-543">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-544">
          <div className="text-center mb-16" data-id="element-545">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-546">
              <div className="w-8 h-px bg-gold-500" data-id="element-547" />
              <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-548">
                Our Capability
              </span>
              <div className="w-8 h-px bg-gold-500" data-id="element-549" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900" data-id="element-550">
              A trusted partner to global leadership teams
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-200" data-id="element-551">
            {[{
            icon: Globe,
            label: 'Global Talent Network',
            detail: '42 countries · 6 regional offices'
          }, {
            icon: Users,
            label: 'Executive Search Specialists',
            detail: 'Senior consultants with industry depth'
          }, {
            icon: Briefcase,
            label: 'C-Suite & Board Recruitment',
            detail: 'CEO, CFO, CHRO, CTO, and board mandates'
          }, {
            icon: TrendingUp,
            label: 'International Hiring Support',
            detail: 'Cross-border placements & advisory'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="px-6 py-8 md:py-4 text-center md:text-left" data-id="element-552">
                <item.icon className="h-7 w-7 text-gold-500 mx-auto md:mx-0 mb-4" data-id="element-553" />
                <h3 className="text-lg font-serif font-bold text-navy-900 mb-2 leading-snug" data-id="element-554">
                  {item.label}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-555">
                  {item.detail}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── SERVICES ──────────────────────────────────────────── */}
      <section className="py-28 bg-gray-50" data-id="element-556">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-557">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-558">
            <div className="lg:col-span-5" data-id="element-559">
              <div className="flex items-center gap-3 mb-6" data-id="element-560">
                <div className="w-12 h-px bg-gold-500" data-id="element-561" />
                <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-562">
                  Practice Areas
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight" data-id="element-563">
                Advisory built around the most consequential hires.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end" data-id="element-564">
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-565">
                From CEO succession to board composition to strategic capability
                builds, our consultants work as long-term partners to leadership
                teams — not transactional recruiters.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200" data-id="element-566">
            {[{
            num: '01',
            title: 'Executive Search',
            desc: 'Confidential CEO, C-suite, and senior leadership mandates for public, private, and PE-backed organizations.'
          }, {
            num: '02',
            title: 'Board Recruitment',
            desc: 'Independent director searches, board composition advisory, and governance consulting.'
          }, {
            num: '03',
            title: 'Leadership Advisory',
            desc: 'Succession planning, executive assessment, and onboarding for new leaders.'
          }, {
            num: '04',
            title: 'Resume & Career Positioning',
            desc: 'Executive-level personal branding, resume strategy, and LinkedIn positioning for senior professionals.'
          }, {
            num: '05',
            title: 'Talent Acquisition Consulting',
            desc: 'In-house TA function design, employer branding, and high-volume hiring strategy.'
          }, {
            num: '06',
            title: 'Global Mobility',
            desc: 'Cross-border placement, work authorization advisory, and international relocation support.'
          }].map((service, index) => <motion.div key={index} initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-white p-10 group hover:bg-navy-900 transition-colors duration-500 cursor-pointer" data-id="element-567">
                <div className="flex items-center justify-between mb-8" data-id="element-568">
                  <span className="text-3xl font-serif text-gold-500 group-hover:text-gold-500 transition-colors" data-id="element-569">
                    {service.num}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-gray-300 group-hover:text-gold-500 transition-colors" data-id="element-570" />
                </div>
                <h3 className="text-xl font-serif font-bold text-navy-900 group-hover:text-white mb-4 transition-colors leading-tight" data-id="element-571">
                  {service.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-300 transition-colors leading-relaxed text-sm" data-id="element-572">
                  {service.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── INDUSTRIES ────────────────────────────────────────── */}
      <section className="py-28 bg-white" data-id="element-573">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-574">
          <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-575">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-576">
              <div className="w-12 h-px bg-gold-500" data-id="element-577" />
              <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-578">
                Sector Expertise
              </span>
              <div className="w-12 h-px bg-gold-500" data-id="element-579" />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-6" data-id="element-580">
              Deep specialization across the industries shaping tomorrow.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-581">
              Each practice is led by consultants with operating experience in
              the sectors they serve.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-gray-200" data-id="element-582">
            {[{
            icon: Cpu,
            name: 'Technology',
            desc: 'Software, SaaS, cybersecurity, cloud infrastructure'
          }, {
            icon: Plane,
            name: 'Aerospace',
            desc: 'Defense, commercial aviation, space technology'
          }, {
            icon: HeartPulse,
            name: 'Healthcare',
            desc: 'Biotech, medical devices, health systems'
          }, {
            icon: DollarSign,
            name: 'Finance',
            desc: 'Banking, asset management, fintech, insurance'
          }, {
            icon: Factory,
            name: 'Manufacturing',
            desc: 'Industrial, automotive, supply chain'
          }, {
            icon: Sparkles,
            name: 'AI & Emerging Technology',
            desc: 'Machine learning, robotics, quantum computing'
          }].map((ind, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-white p-10 group hover:bg-gray-50 transition-colors duration-300 relative overflow-hidden cursor-pointer" data-id="element-583">
                <div className="absolute top-0 left-0 h-1 w-0 group-hover:w-full bg-gold-500 transition-all duration-500" data-id="element-584" />
                <ind.icon className="h-8 w-8 text-navy-900 mb-6 group-hover:text-gold-500 transition-colors" data-id="element-585" />
                <h3 className="text-xl font-serif font-bold text-navy-900 mb-3" data-id="element-586">
                  {ind.name}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-587">
                  {ind.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ──────────────────────────────────────── */}
      <section className="py-28 bg-navy-900 text-white relative overflow-hidden" data-id="element-588">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-589" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" data-id="element-590" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12" data-id="element-591">
          <div className="flex items-center gap-3 mb-12 justify-center" data-id="element-592">
            <div className="w-12 h-px bg-gold-500" data-id="element-593" />
            <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-594">
              Client Voices
            </span>
            <div className="w-12 h-px bg-gold-500" data-id="element-595" />
          </div>

          {/* Large featured quote */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto text-center mb-20" data-id="element-596">
            <div className="text-gold-500 font-serif text-7xl leading-none mb-6" data-id="element-597">
              "
            </div>
            <p className="text-2xl md:text-3xl font-serif italic leading-relaxed mb-10 text-white" data-id="element-598">
              TalentBridge delivered a CEO candidate who reshaped our
              trajectory. The quality of judgment they brought to the search was
              unlike any firm we'd worked with before.
            </p>
            <div className="flex items-center justify-center gap-4" data-id="element-599">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gold-500 to-gold-600 flex items-center justify-center text-navy-900 font-serif text-lg font-bold" data-id="element-600">
                MR
              </div>
              <div className="text-left" data-id="element-601">
                <div className="font-bold text-white" data-id="element-602">Margaret Rosenthal</div>
                <div className="text-sm text-gray-400" data-id="element-603">
                  Board Chair · Fortune 500 Industrial
                </div>
              </div>
            </div>
          </motion.div>

          {/* Supporting quotes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-604">
            {[{
            quote: 'Their global reach and discretion were essential for a sensitive cross-border CFO search. Flawless execution.',
            author: 'Henrik Voss',
            role: 'Managing Partner',
            company: 'European PE Firm',
            initials: 'HV'
          }, {
            quote: 'The board composition advisory transformed how we think about governance. Strategic, thoughtful, and rigorous.',
            author: 'Aisha Bashir',
            role: 'Independent Director',
            company: 'Global Healthcare',
            initials: 'AB'
          }, {
            quote: 'A genuine partnership — not transactional. They understand our culture and only present candidates who fit.',
            author: 'Daniel Kovacs',
            role: 'CHRO',
            company: 'Tech Unicorn',
            initials: 'DK'
          }].map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="border-l-2 border-gold-500/40 pl-6" data-id="element-605">
                <p className="text-gray-300 leading-relaxed mb-6 italic" data-id="element-606">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3" data-id="element-607">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-gold-500 font-bold text-sm" data-id="element-608">
                    {t.initials}
                  </div>
                  <div data-id="element-609">
                    <div className="font-semibold text-white text-sm" data-id="element-610">
                      {t.author}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-611">
                      {t.role} · {t.company}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA — Partner With TalentBridge ─────────────── */}
      <section className="py-28 bg-white" data-id="element-612">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-613">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-614">
            {/* Left: Pitch */}
            <div data-id="element-615">
              <div className="flex items-center gap-3 mb-6" data-id="element-616">
                <div className="w-12 h-px bg-gold-500" data-id="element-617" />
                <span className="text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-618">
                  Begin a Conversation
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 leading-tight mb-8" data-id="element-619">
                Partner With <span className="italic" data-id="element-620">TalentBridge</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-10" data-id="element-621">
                Whether you are building a board, recruiting your next CEO, or
                considering your own next move — our consultants are available
                for a confidential, no-obligation conversation.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-622">
                {['Confidential initial consultation', 'Senior consultant from your sector', 'No-commitment scoping conversation', 'Response within one business day'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-623">
                    <CheckCircle className="h-5 w-5 text-gold-500 mt-0.5 flex-shrink-0" data-id="element-624" />
                    <span className="text-gray-700" data-id="element-625">{item}</span>
                  </li>)}
              </ul>

              <div className="pt-8 border-t border-gray-200" data-id="element-626">
                <div className="text-xs uppercase tracking-widest text-gray-400 mb-2" data-id="element-627">
                  Direct Line
                </div>
                <a href="tel:+16788073391" className="text-2xl font-serif font-bold text-navy-900 hover:text-gold-600 transition-colors" data-id="element-628">
                  +1 (678) 807-3391
                </a>
                <div className="text-sm text-gray-500 mt-2" data-id="element-629">
                  Mon–Fri, 9:00am – 6:00pm EST
                </div>
              </div>
            </div>

            {/* Right: Consultation form */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-sm border border-gray-200 shadow-xl overflow-hidden" data-id="element-630">
              <div className="bg-navy-900 text-white px-8 py-7" data-id="element-631">
                <h3 className="text-2xl font-serif font-bold mb-1" data-id="element-632">
                  Request a Consultation
                </h3>
                <p className="text-sm text-gray-400" data-id="element-633">
                  All inquiries are held in strict confidence.
                </p>
              </div>
              <iframe title="Request a consultation" src="https://form.jotform.com/261658116470559" className="w-full min-h-[620px] border-0 block" scrolling="no" data-id="element-634" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}